// lib/guideSignals.ts
// Consolidated per-concept localStorage store for guide signals.
// Migrates from legacy individual keys on first read.

export type SignalKey =
  | 'pdfOpenCount'
  | 'hasHighlights'
  | 'hasExcerpts'
  | 'hasUsedSmartTrawl'
  | 'hasUsedPositionAnalysis'
  | 'hasUsedDismissSimilar'
  | 'hasUsedResearchInbox'
  | 'hasTaggedOrNotedPaper'
  | 'hasUsedDynamicChat'
  | 'hasRunCoverageAudit'
  | 'hasOpenedWritingWorkspace'
  | 'hasUsedMonitorTriage'
  | 'hasOverriddenScore'
  | 'hasRefinedConcept'
  | 'hasRescoredAfterRefine'
  | 'hasViewedLandscape'
  | 'hasDrilledIntoCamp';

export type SignalsShape =
  & { pdfOpenCount: number }
  & Record<Exclude<SignalKey, 'pdfOpenCount'>, boolean>;

// Internal shape stored in localStorage — tracks whether legacy migration ran
// so we don't repeatedly clobber newly-written consolidated values with stale
// legacy keys that may briefly reappear (e.g. from older code paths).
type StoredShape = SignalsShape & { _migrated?: boolean };

const DEFAULTS: SignalsShape = {
  pdfOpenCount: 0,
  hasHighlights: false,
  hasExcerpts: false,
  hasUsedSmartTrawl: false,
  hasUsedPositionAnalysis: false,
  hasUsedDismissSimilar: false,
  hasUsedResearchInbox: false,
  hasTaggedOrNotedPaper: false,
  hasUsedDynamicChat: false,
  hasRunCoverageAudit: false,
  hasOpenedWritingWorkspace: false,
  hasUsedMonitorTriage: false,
  hasOverriddenScore: false,
  hasRefinedConcept: false,
  hasRescoredAfterRefine: false,
  hasViewedLandscape: false,
  hasDrilledIntoCamp: false,
};

const consolidatedKey = (conceptId: string) => `paperchase-guide-signals-${conceptId}`;

const LEGACY_KEY_MAP = (
  conceptId: string,
): Array<{ legacyKey: string; target: SignalKey; parse: (raw: string) => boolean | number }> => [
  {
    legacyKey: `paperchase-pdf-opens-${conceptId}`,
    target: 'pdfOpenCount',
    parse: (raw) => Number.parseInt(raw, 10) || 0,
  },
  {
    legacyKey: `paperchase-has-highlights-${conceptId}`,
    target: 'hasHighlights',
    parse: (raw) => raw === 'true' || raw === '1',
  },
  {
    legacyKey: `paperchase-smart-trawl-used-${conceptId}`,
    target: 'hasUsedSmartTrawl',
    parse: (raw) => raw === 'true' || raw === '1',
  },
  {
    legacyKey: `paperchase-position-analysis-used-${conceptId}`,
    target: 'hasUsedPositionAnalysis',
    parse: (raw) => raw === 'true' || raw === '1',
  },
];

function readConsolidatedRaw(conceptId: string): StoredShape {
  try {
    const raw = localStorage.getItem(consolidatedKey(conceptId));
    if (!raw) return { ...DEFAULTS };
    const parsed = JSON.parse(raw);
    return { ...DEFAULTS, ...parsed };
  } catch {
    return { ...DEFAULTS };
  }
}

function stripInternal(stored: StoredShape): SignalsShape {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { _migrated, ...rest } = stored;
  return rest as SignalsShape;
}

function writeConsolidated(conceptId: string, value: StoredShape): void {
  try {
    localStorage.setItem(consolidatedKey(conceptId), JSON.stringify(value));
  } catch {
    // localStorage might throw (quota, disabled) — swallow silently
  }
}

function migrateLegacy(conceptId: string): StoredShape | null {
  const current = readConsolidatedRaw(conceptId);
  if (current._migrated) return null;

  const legacyEntries = LEGACY_KEY_MAP(conceptId);
  const hits = legacyEntries.filter((e) => localStorage.getItem(e.legacyKey) !== null);

  const merged: StoredShape = { ...current, _migrated: true };
  for (const hit of hits) {
    const raw = localStorage.getItem(hit.legacyKey)!;
    (merged as Record<string, boolean | number>)[hit.target] = hit.parse(raw);
    localStorage.removeItem(hit.legacyKey);
  }
  writeConsolidated(conceptId, merged);
  return merged;
}

export const guideSignals = {
  read(conceptId: string): SignalsShape {
    const migrated = migrateLegacy(conceptId);
    if (migrated) return stripInternal(migrated);
    return stripInternal(readConsolidatedRaw(conceptId));
  },

  mark(conceptId: string, key: SignalKey, value: boolean | number = true): void {
    const current = readConsolidatedRaw(conceptId);
    (current as Record<string, boolean | number>)[key] = value;
    writeConsolidated(conceptId, current);
  },

  increment(conceptId: string, key: 'pdfOpenCount'): void {
    const current = readConsolidatedRaw(conceptId);
    current[key] = current[key] + 1;
    writeConsolidated(conceptId, current);
  },

  clear(conceptId: string): void {
    try {
      localStorage.removeItem(consolidatedKey(conceptId));
    } catch {
      // swallow
    }
  },
};
